import { prepareCssAngle, getRectAngleBound } from '../angle-utils.js';
import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Render a repeating linear gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingLinearGradient(context, gradientDef, { x, y, w, h }) {
  const angle = prepareCssAngle(gradientDef.orientation);

  // Calculate boundary points using width and height
  const startBound = getRectAngleBound(w, h, angle.revTheta);
  const endBound = getRectAngleBound(w, h, angle.theta);

  const start = { x: startBound.x, y: startBound.y };
  const end = { x: endBound.x, y: endBound.y };

  const gradient = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate gradient pattern total length
  let patternLength = 0;
  colorStops.forEach((stop) => {
    if (stop.length && stop.length.type === 'px') {
      patternLength = Math.max(patternLength, parseFloat(stop.length.value));
    }
  });

  // Calculate number of repetitions needed
  const distance = Math.hypot(end.x - start.x, end.y - start.y);
  const repetitions = Math.ceil(distance / patternLength);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    colorStops.forEach((stop, index) => {
      let position = i / repetitions;

      if (stop.length) {
        if (stop.length.type === 'px') {
          position += parseFloat(stop.length.value) / distance / repetitions;
        } else {
          position += parseFloat(stop.length.value) / 100 / repetitions;
        }
      } else {
        position += index / (colorStops.length - 1) / repetitions;
      }

      const colorValue = colorStopToString(stop);

      if (colorValue) {
        gradient.addColorStop(Math.min(position, 1), colorValue);
      }
    });
  }

  return gradient;
}
